/* eslint-disable newline-per-chained-call */
import * as Yup from 'yup';
import urlRegex from 'url-regex';
import { EntGeographyList, InvestorGeographyList, InvestorIndustryOptionArray, EntIndustryOptionArray, marketModelList } from '../types/types';


const stateList = EntGeographyList.map((i) => i.state).filter((i) => i !== '');
const countryList = EntGeographyList.map((i) => i.country).filter((i) => i !== '');

export const phoneRegEx = /^(?:\D*\d){10,15}\D*$/;
export const phoneError = 'Must be 10-15 digits.';
export const maxEntIndustries = 5;
export const maxIndustriesError = 'Select 5 or fewer industries.';
const minIndustries = 1;
const minIndustriesError = 'At least one industry is required.';
const urlErrorMessage = 'Must be a valid url.';

export const optionalUrlTest = Yup.string().nullable().trim().test('is-url-optional', urlErrorMessage, (value) => {
  if (!value) {
    return true;
  }
  const regEx = urlRegex({ strict: false });
  return regEx.test(value);
});

export const requiredUrlTest = Yup.string().trim().test('is-url-required', urlErrorMessage, (value) => {
  if (!value) {
    return false;
  }
  const regEx = urlRegex({ strict: false });
  return regEx.test(value);
});


export const investorPublishedMarketModelSchema = Yup.object().required().shape({
  B2C: Yup.string().oneOf(['Never', 'Sometimes', 'Only']).required(),
  B2B: Yup.string().oneOf(['Never', 'Sometimes', 'Only']).required(),
  B2G: Yup.string().oneOf(['Never', 'Sometimes', 'Only']).required(),
  SaaS: Yup.string().oneOf(['Never', 'Sometimes', 'Only']).required()
}).required();

export const investorPublishedIndustryListSchema = Yup.array().min(minIndustries, minIndustriesError).of(Yup.object().shape({
  industry: Yup.string().oneOf(InvestorIndustryOptionArray)
})).required();

export const investorPublishedGeographyListSchema = Yup.array().of(Yup.object().shape({
  state: Yup.string().nullable().oneOf([...stateList, '']),
  country: Yup.string().nullable().oneOf([...countryList, '']),
  worldRegion: Yup.string().oneOf(InvestorGeographyList.map((i) => i.worldRegion).filter((i) => i !== ''))
})).required();

export const investorPublishedIntroSchema = Yup.object({
  contactName: Yup.string().required().trim(),
  companyName: Yup.string().required().trim(),
  linkedinProfile: optionalUrlTest,
  intro: Yup.string().required().trim(),
  email: Yup.string().email().required()
}).required();

export const investorPublishedInvestmentCriteriaSchema = Yup.object({
  investmentLow: Yup.number().typeError('Required').min(0, 'Minimum $0.').required(),
  investmentHigh: Yup.number().typeError('Required').min(Yup.ref('investmentLow'), 'Must be greater than or equal to Investment Low.').required(),
  investmentRevenue: Yup.number().typeError('Required').required(),
  founderFemale: Yup.boolean().required(),
  founderUnderrepresented: Yup.boolean().required(),
  geographyList: investorPublishedGeographyListSchema,
  industryList: investorPublishedIndustryListSchema,
  marketModelCriteria: investorPublishedMarketModelSchema
});

export const entUnpublishedIntroSchema = Yup.object().shape({
  name: Yup.string().trim(),
  intro: Yup.string().max(1500, 'Maximum 1500 Characters').optional().trim(),
  geography: Yup.object().shape({
    id: Yup.number().nullable().optional(),
    state: Yup.string().nullable().oneOf([...stateList, '']),
    country: Yup.string().nullable().oneOf([...countryList, '']),
    worldRegion: Yup.string().oneOf(EntGeographyList.map((i) => i.worldRegion).filter((i) => i !== '')).required()
  }).optional().nullable(),
  marketModelList: Yup.array().of(Yup.string().oneOf(marketModelList)),
  elevatorPitchUrl: optionalUrlTest,
  fullPitchUrl: optionalUrlTest,
  linkedinProfileUrl: optionalUrlTest,
  executiveSummaryUrl: Yup.string().url().optional().nullable(),
  pitchDeckUrl: Yup.string().url().optional().nullable(),
  industryList: Yup.array().max(maxEntIndustries, maxIndustriesError).of(Yup.object().shape({
    id: Yup.number().nullable(),
    industry: Yup.string().oneOf(EntIndustryOptionArray)
  }))
});

export const entPublishedIntroSchema = Yup.object().shape({
  name: Yup.string().required().trim(),
  intro: Yup.string().max(1500, 'Maximum 1500 Characters').required().trim(),
  geography: Yup.object().shape({
    state: Yup.string().nullable().oneOf([...stateList, '']),
    country: Yup.string().nullable().oneOf([...countryList, '']),
    worldRegion: Yup.string().oneOf(EntGeographyList.map((i) => i.worldRegion).filter((i) => i !== '')).required()
  }).required(),
  marketModelList: Yup.array().of(Yup.string().oneOf(marketModelList)).required(),
  elevatorPitchUrl: optionalUrlTest,
  fullPitchUrl: optionalUrlTest,
  linkedinProfileUrl: requiredUrlTest,
  executiveSummaryUrl: Yup.string().url().required(),
  pitchDeckUrl: Yup.string().url().required(),
  industryList: Yup.array().max(maxEntIndustries, maxIndustriesError).min(minIndustries, minIndustriesError).of(Yup.object().shape({
    industry: Yup.string().oneOf(EntIndustryOptionArray)
  })).required()
});

export const entPublishedFinancialSummary = Yup.object().shape({
  financeSoughtLow: Yup.number().typeError('Required').min(10000, 'Minimum $10,000.').required(),
  financeSoughtHigh: Yup.number().typeError('Required').min(Yup.ref('financeSoughtLow'), 'Must be greater than or equal to Finance Sought Low.').required(),
  financeRaised: Yup.number().typeError('Required').min(0).required(),
  currentRevenue: Yup.number().typeError('Required').required()
});

export const entPublishedContactInformation = Yup.object({
  contactName: Yup.string().required().trim(),
  contactTitle: Yup.string().required().trim(),
  street1: Yup.string().required().trim(),
  street2: Yup.string().optional().nullable().trim(),
  city: Yup.string().required().trim(),
  state: Yup.string().required().trim(),
  country: Yup.string().required().trim(),
  zipCode: Yup.string().required().trim(),
  email: Yup.string().email().required(),
  phone: Yup.string().matches(phoneRegEx, phoneError).required(),
  website: requiredUrlTest
});

export const entPublishedCompanyDetails = Yup.object({
  managementTeam: Yup.array().of(Yup.object().shape({
    name: Yup.string().required('Name is required').typeError('Name is required.').trim(),
    title: Yup.string().required().typeError('Title is required.').trim(),
    intro: Yup.string().optional().nullable().trim()
  })).required(),
  employeeCount: Yup.number().min(0).required(),
  bank: Yup.string().optional().nullable().trim(),
  auditor: Yup.string().optional().nullable().trim(),
  lawFirm: Yup.string().optional().nullable().trim(),
  capTableManagement: Yup.string().optional().nullable().trim(),
  hrProvider: Yup.string().optional().nullable().trim(),
  currentInvestor: Yup.string().optional().nullable().trim(),
  fundUsage: Yup.string().required().trim(),
  founderExit: Yup.boolean().required(),
  founderFemale: Yup.boolean().required(),
  founderUnderrepresented: Yup.boolean().required()
});

export const entPublishedExecSummary = Yup.object({
  background: Yup.string().required().trim(),
  management: Yup.string().required().trim(),
  market: Yup.string().required().trim(),
  distribution: Yup.string().required().trim(),
  competition: Yup.string().required().trim(),
  financialProjection: Yup.array().of(
    Yup.object().shape({
      year: Yup.number().typeError('Required').required(),
      revenue: Yup.number().typeError('Required').required(),
      EBITDA: Yup.number().typeError('Required').required()
    })
  ).required().min(5)
});

export const entPublishedContactPersonPhotoSchema = Yup.object({
  profileImageUrl: Yup.string().url().required()
});

export const entPublishedCompanyLogoSchema = Yup.object({
  logo: Yup.string().url().required()
});

export const connectorProfileSchema = Yup.object({
  firstName: Yup.string().required().trim(),
  lastName: Yup.string().required().trim(),
  companyName: Yup.string().required().trim(),
  email: Yup.string().email().required().trim(),
  introduction: Yup.string().required().trim(),
  profileImageUrl: Yup.string().url().required().trim(),
  linkedInProfileUrl: requiredUrlTest,
  referralExpectation: Yup.string().required().trim()
});

export const connectorProfileFirstNameSchema = Yup.object({
  firstName: Yup.string().required().trim()
});

export const connectorProfileLastNameSchema = Yup.object({
  lastName: Yup.string().required().trim()
});

export const connectorProfileCompanyNameSchema = Yup.object({
  companyName: Yup.string().required().trim()
});

export const connectorProfileEmailSchema = Yup.object({
  email: Yup.string().email().required().trim()
});

export const connectorProfileSummarySchema = Yup.object({
  introduction: Yup.string().required().trim()
});

export const connectorProfileImageSchema = Yup.object({
  profileImageUrl: Yup.string().url().required().trim()
});

export const connectorProfileLinkedinSchema = Yup.object({
  linkedInProfileUrl: requiredUrlTest
});

export const connectorRefferalExpectationSchema = Yup.object({
  referralExpectation: Yup.string().required().trim()
});

